import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import investmentTypeLang from '../../features/fund-type/langs/investmentTypeLang';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import styleVariables from '../../../../app/styles/styleVariables';
import investmentTypeDomainLang from '../../features/fund-type/langs/investmentTypeDomainLang';
import IFundType from '../../interfaces/expertise/IFundType';
import React from 'react';

const mapFundTypes: IFundType[] = [
	{
		tag: 'real_estate',
		name: investmentTypeLang.real_estate.tag,
		icon: <AppCustomIcon tag="real_estate" />,
		sliceColor: {
			light: styleVariables.sliceColors.lightMode[1],
			dark: styleVariables.sliceColors.darkMode[1],
		},
		domains: [
			{
				tag: 'companies',
				name: investmentTypeDomainLang.companies.tag,
				icon: <AppCustomIcon tag="companies" />,
			},
			{
				tag: 'multi_management',
				name: investmentTypeDomainLang.multi_management.tag,
				icon: <AppCustomIcon tag="multi_management" />,
			},
			{
				tag: 'recovery_french_bond',
				name: investmentTypeDomainLang.recovery_french_bond.tag,
				icon: <AppCustomIcon tag="recovery_french_bond" />,
			},
		],
		fundOnglets: ['assets'],
		expertiseOnglets: ['synthesis', 'portfolio', 'history'],
		assetsDisplay: 'list',
		expertiseHistoryBlocks: [
			'timeline',
			'jcurve',
			'total_value',
			'multiple_net_of_fees',
			'last_inflows_outflows_engagements',
		],
		fundInformations: {
			general: [
				'juridical_status',
				'isin',
				'currency',
				'targeted_performance',
				'admin_fees_num',
				'distribution_frequency',
			],
			planning: [
				'creation_date',
				'subscription_end_date',
				'capital_call_end_date',
				'investment_end_date',
				'amortization_end_date',
				'liquidation_date',
			],
			roles: ['management_company', 'fund_administrator', 'depository', 'accountants'],
			strategy: ['domainTag', 'sfdr'],
		},
	},
	{
		tag: 'private_credit',
		name: investmentTypeLang.private_credit.tag,
		icon: <AppCustomIcon tag="private_credit" />,
		sliceColor: {
			light: styleVariables.sliceColors.lightMode[11],
			dark: styleVariables.sliceColors.darkMode[11],
		},
		domains: [
			{
				tag: 'real_estate',
				name: investmentTypeDomainLang.real_estate.tag,
				icon: <AppCustomIcon tag="real_estate" />,
			},
			{
				tag: 'energy_transition',
				name: investmentTypeDomainLang.energy_transition.tag,
				icon: <AppCustomIcon tag="energy_transition" />,
			},
			{
				tag: 'public',
				name: investmentTypeDomainLang.public.tag,
				icon: <AppCustomIcon tag="public" />,
			},
			{
				tag: 'granular',
				name: investmentTypeDomainLang.granular.tag,
				icon: <AppCustomIcon tag="granular" />,
			},
			{
				tag: 'companies',
				name: investmentTypeDomainLang.companies.tag,
				icon: <AppCustomIcon tag="companies" />,
			},
			{
				tag: 'multi_management',
				name: investmentTypeDomainLang.multi_management.tag,
				icon: <AppCustomIcon tag="multi_management" />,
			},
			{
				tag: 'recovery_french_bond',
				name: investmentTypeDomainLang.recovery_french_bond.tag,
				icon: <AppCustomIcon tag="recovery_french_bond" />,
			},
		],
		expertiseOnglets: ['synthesis', 'portfolio', 'history'],
		expertiseHistoryBlocks: ['timeline', 'jcurve', 'total_value', 'last_inflows_outflows_engagements'],
		fundOnglets: ['performance', 'documents', 'operations', 'informations', 'assets'],
		assetsDisplay: 'table',
		fundInformations: {
			general: [
				'juridical_status',
				'isin',
				'currency',
				'targeted_performance',
				'admin_fees_num',
				'distribution_frequency',
			],
			planning: [
				'creation_date',
				'subscription_end_date',
				'capital_call_end_date',
				'investment_end_date',
				'amortization_end_date',
				'liquidation_date',
			],
			roles: ['management_company', 'fund_administrator', 'depository', 'accountants'],
			strategy: ['domainTag', 'sfdr'],
		},
	},
	{
		tag: 'venture_capital',
		name: investmentTypeLang.venture_capital.tag,
		icon: <AppCustomIcon tag="venture_capital" />,
		sliceColor: {
			light: styleVariables.sliceColors.lightMode[4],
			dark: styleVariables.sliceColors.darkMode[4],
		},
		domains: [
			{
				tag: 'real_estate',
				name: investmentTypeDomainLang.real_estate.tag,
				icon: <AppCustomIcon tag="real_estate" />,
			},
			{
				tag: 'energy_transition',
				name: investmentTypeDomainLang.energy_transition.tag,
				icon: <AppCustomIcon tag="energy_transition" />,
			},
			{
				tag: 'companies',
				name: investmentTypeDomainLang.companies.tag,
				icon: <AppCustomIcon tag="companies" />,
			},
		],
		expertiseOnglets: ['synthesis', 'assets', 'history'],
		expertiseHistoryBlocks: ['timeline', 'jcurve', 'multiple_net_of_fees', 'last_inflows_outflows_engagements'],
		fundOnglets: ['performance', 'documents', 'operations', 'informations', 'assets'],
		assetsDisplay: 'list',
		fundInformations: {
			general: ['juridical_status', 'isin', 'currency', 'hurdle', 'admin_fees_text'],
			planning: ['creation_date', 'closing_dates', 'investment_end_date', 'liquidation_date'],
			roles: ['management_company', 'consulting_company', 'fund_administrator', 'depository', 'accountants'],
			strategy: ['domainText', 'sfdr'],
		},
	},
	{
		tag: 'private_equity',
		name: investmentTypeLang.private_equity.tag,
		icon: <AppCustomIcon tag="private_equity" />,
		sliceColor: {
			light: styleVariables.sliceColors.lightMode[3],
			dark: styleVariables.sliceColors.darkMode[3],
		},
		domains: [
			{
				tag: 'real_estate',
				name: investmentTypeDomainLang.real_estate.tag,
				icon: <AppCustomIcon tag="real_estate" />,
			},
			{
				tag: 'energy_transition',
				name: investmentTypeDomainLang.energy_transition.tag,
				icon: <AppCustomIcon tag="energy_transition" />,
			},
			{
				tag: 'companies',
				name: investmentTypeDomainLang.companies.tag,
				icon: <AppCustomIcon tag="companies" />,
			},
		],
		expertiseOnglets: ['synthesis', 'assets', 'history'],
		expertiseHistoryBlocks: ['timeline', 'jcurve', 'multiple_net_of_fees', 'last_inflows_outflows_engagements'],
		fundOnglets: ['performance', 'documents', 'operations', 'informations', 'assets'],
		assetsDisplay: 'list',
		fundInformations: {
			general: ['juridical_status', 'isin', 'currency', 'hurdle', 'admin_fees_text'],
			planning: ['creation_date', 'closing_dates', 'investment_end_date', 'liquidation_date'],
			roles: ['management_company', 'consulting_company', 'fund_administrator', 'depository', 'accountants'],
			strategy: ['domainText', 'sfdr'],
		},
	},
	{
		tag: 'listed_assets',
		name: investmentTypeLang.listed_assets.tag,
		icon: <AppCustomIcon tag="listed_assets" />,
		sliceColor: {
			light: styleVariables.sliceColors.lightMode[0],
			dark: styleVariables.sliceColors.darkMode[0],
		},
		domains: [],
		expertiseOnglets: ['synthesis', 'assets'],
		expertiseHistoryBlocks: [],
		fundOnglets: ['performance', 'documents', 'informations', 'assets'],
		assetsDisplay: 'list',
		fundInformations: {
			general: [
				'juridical_status',
				'isin',
				'currency',
				'agreement_date',
				'classification_AMF',
				'risk_level',
				'launching_date',
			],
			strategy: ['asset_class', 'sfdr', 'thematic', 'investment_horizon'],
			labels: [],
			roles: ['management_company', 'valorizer', 'depository', 'accountants'],
		},
	},
];

const useInvestExpertises = () => {
	const { t } = useTranslation();

	const result = useMemo<IFundType[]>(
		() =>
			mapFundTypes.map((x) => ({
				...x,
				name: t(x.name),
			})),
		[t],
	);

	return result;
};

export default useInvestExpertises;
